import type { DrawerProps as MuiDrawerProps } from '@mui/material';
import { Divider, IconButton, Drawer as MuiDrawer, styled } from '@mui/material';
import { getSelectorAttribute, integrationsPageSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import type { ReactNode } from 'react';
import { forwardRef, useImperativeHandle, useCallback, useState } from 'react';
import { SuspenseBoundary } from '../../../../components/utility/SuspenseBoundary';

type DrawerProps = {
  anchor: MuiDrawerProps['anchor'];
  children: ReactNode;
  footer?: ReactNode;
  stickyFooter?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

export type IntegrationDrawerRef = {
  openDrawer: () => void;
  closeDrawer: () => void;
};

export const IntegrationDrawer = forwardRef<IntegrationDrawerRef, DrawerProps>(
  ({ anchor, children, footer, onClose, onOpen, stickyFooter = true }, ref) => {
    const [open, setOpen] = useState(false);

    const openDrawer = useCallback(() => {
      onOpen?.();
      setOpen(true);
    }, [onOpen, setOpen]);

    const closeDrawer = useCallback(() => {
      setOpen(false);
      onClose?.();
    }, [onClose, setOpen]);

    useImperativeHandle(ref, () => ({
      openDrawer,
      closeDrawer,
    }));

    return (
      <MuiDrawer anchor={anchor} open={open} onClose={closeDrawer}>
        <DrawerContainer>
          <SuspenseBoundary>
            <ContentWrapper allowOverflow={stickyFooter}>
              <CloseDrawerButtonContainer>
                <IconButton
                  onClick={closeDrawer}
                  {...getSelectorAttribute(integrationsPageSelectors.drawer.closeButton)}
                >
                  <VyIcon icon="CloseBig" />
                </IconButton>
              </CloseDrawerButtonContainer>
              <ContentContainer>{children}</ContentContainer>
            </ContentWrapper>
            {footer && (
              <div>
                <Divider />
                <FooterContainer>{footer}</FooterContainer>
              </div>
            )}
          </SuspenseBoundary>
        </DrawerContainer>
      </MuiDrawer>
    );
  },
);

const DrawerContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
  width: '550px',
});

type ContentWrapperProps = { allowOverflow: boolean };
const ContentWrapper = styled('div')<ContentWrapperProps>(({ allowOverflow }) => ({
  ...(allowOverflow && { overflowY: 'auto' }),
  padding: '40px 32px',
}));

const CloseDrawerButtonContainer = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
});

const ContentContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const FooterContainer = styled('div')({
  padding: '16px 32px',
});
